import React, { useEffect, useState } from 'react';
import { useHistory, useLocation, Link } from 'react-router-dom';
import Menu from '../../components/Menu/Menu';
import Pagination, { getStoredPageSize } from '../../components/Pagination/Pagination';
import Footer from '../../components/Footer/Footer';
import { getAutomations, getAutomationsCounts, startAutomation, stopAutomation, deleteAutomation, promoteAutomation, copyAutomationToTest, copyAllAutomationsToTest, runAutomation } from '../../services/AutomationsService';
import { getStrategiesUsage, provisionAiSignal } from '../../services/StrategiesService';
import { getItemsHealth } from '../../services/BeholderService';
import { processError } from '../../services/ServiceUtils';
import AutomationRow from './AutomationRow';
import SelectStrategy from '../../components/SelectStrategy/SelectStrategy';
import AutomationModal from './AutomationModal/AutomationModal';
import Toast from '../../components/Toast/Toast';
import NewAutomationButton from './NewAutomationButton';
import CompletarAutomacoesButton from './CompletarAutomacoesButton';
import GridModal from './GridModal/GridModal';
import { getEmptyMessage, getErrorMessage } from '../../utils/MessageUtils';
import { DEFAULT_SYMBOL } from '../../utils/SymbolUtils';
import BacktestButton from './BacktestModal/BacktestButton';
import BacktestModal from './BacktestModal/BacktestModal';
import { findRowFromActionButton, getIdFromActionButton } from '../../utils/DataRowButtonUtils';
import HowItWorks from '../../components/HowItWorks/HowItWorks';
import QuickSetupBanner from '../../components/QuickSetupBanner/QuickSetupBanner';
import LogModal from '../../components/Logs/LogModal';
import SectionHealthBanner from '../../components/SectionHealthBanner/SectionHealthBanner';
import { getCurrentEnvironment, subscribeToCurrentEnvironment } from '../../utils/CurrentEnvironmentStorage';
import { getCurrentMarket, subscribeToCurrentMarket } from '../../utils/MarketStorage';
import { confirmDialog } from '../../utils/ConfirmDialog';
import OnlyActiveToggle from '../../components/OnlyActiveToggle/OnlyActiveToggle';
import { getOnlyActive, setOnlyActive as storeOnlyActive } from '../../utils/OnlyActiveStorage';
import ViewModeToggle, { getStoredViewMode } from '../../components/ViewModeToggle/ViewModeToggle';

const AUTOMATION_ISSUE_IDS = ['ai_scan_inactive', 'no_ai_scan'];

function Automations() {

    const defaultLocation = useLocation();

    const history = useHistory();

    const [automations, setAutomations] = useState([]);
    const [count, setCount] = useState(0);
    const [prodCounts, setProdCounts] = useState(null);
    const [copyingAll, setCopyingAll] = useState(false);
    const [page, setPage] = useState(getPage());
    const [size, setSize] = useState(getSize());
    const [usageMap, setUsageMap] = useState({});
    const [healthMap, setHealthMap] = useState({});
    const [environment, setEnvironment] = useState(getCurrentEnvironment());
    const [market, setMarket] = useState(getCurrentMarket());
    const [strategyId, setStrategyId] = useState('');
    const [onlyActive, setOnlyActiveState] = useState(getOnlyActive('automations'));
    // Modo Simples (padrão): só automações avulsas, escondendo o que é gerenciado por estratégia.
    const [viewMode, setViewMode] = useState(getStoredViewMode('automationsViewMode'));
    const [hiddenCount, setHiddenCount] = useState(0);
    // Pegadinha: se o usuário filtrou por uma estratégia específica, standalone teria que
    // dar sempre vazio (estratégia = justamente o que fica de fora do modo Simples). Nesse
    // caso mostramos as automações da estratégia normalmente, mesmo em modo Simples.
    const standalone = viewMode === 'simple' && !strategyId;

    useEffect(() => subscribeToCurrentEnvironment(setEnvironment), []);
    useEffect(() => subscribeToCurrentMarket(setMarket), []);

    const [notification, setNotification] = useState(getEmptyMessage());

    const DEFAULT_AUTOMATION = {
        symbol: DEFAULT_SYMBOL,
        conditions: '',
        name: '',
        indexes: '',
        actions: [],
        environment
    };
    const [editAutomation, setEditAutomation] = useState(DEFAULT_AUTOMATION);

    function getPage(location) {
        if (!location) location = defaultLocation;
        return new URLSearchParams(location.search).get('page');
    }

    function getSize(location) {
        if (!location) location = defaultLocation;
        return parseInt(new URLSearchParams(location.search).get('size')) || getStoredPageSize();
    }

    useEffect(() => {
        return history.listen(location => {
            setPage(getPage(location));
            setSize(getSize(location));
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [history]);

    useEffect(() => {
        getAutomations(page || 1, environment, size, strategyId, market, onlyActive, standalone)
            .then(result => {
                setCount(result.count);
                setAutomations(result.rows);
                setHiddenCount(result.hiddenCount || 0);
            })
            .catch(err => setNotification(getErrorMessage(processError(err))))

    }, [page, environment, size, strategyId, market, onlyActive, standalone]);

    function onToggleActive(value) {
        storeOnlyActive('automations', value);
        setOnlyActiveState(value);
        const params = new URLSearchParams(defaultLocation.search);
        params.set('page', '1');
        history.push({ search: params.toString() });
    }

    function onViewModeChange(mode) {
        setViewMode(mode);
        const params = new URLSearchParams(defaultLocation.search);
        params.set('page', '1');
        history.push({ search: params.toString() });
    }

    // Inverte usageMap.automations (automationId → [{id,name}] de estratégias) pra
    // estratégia → contagem de automações que ela gerencia. Usado nos chips do card-resumo.
    function getStrategyChips() {
        const chips = {};
        Object.values(usageMap).forEach(refs => {
            (refs || []).forEach(ref => {
                if (!chips[ref.id]) chips[ref.id] = { id: ref.id, name: ref.name, count: 0 };
                chips[ref.id].count++;
            });
        });
        return Object.values(chips).sort((a, b) => b.count - a.count);
    }

    // SelectStrategy emite 2 eventos (strategyId + automationIds); só reagimos ao strategyId.
    function onStrategyChange(event) {
        if (event.target.id !== 'strategyId') return;
        setStrategyId(event.target.value || '');
    }

    useEffect(() => {
        if (environment === 'test')
            getAutomationsCounts().then(c => setProdCounts(c.production)).catch(() => {});
    }, [environment]);

    useEffect(() => {
        getStrategiesUsage()
            .then(map => setUsageMap(map.automations || {}))
            .catch(() => {});
    }, []);

    useEffect(() => {
        getItemsHealth()
            .then(map => setHealthMap(map.automations || {}))
            .catch(() => {});
    }, []);

    function onNewAutomationClick(event) {
        const option = event.target.id;
        if (option === 'linkSchedule') {
            setEditAutomation({ ...DEFAULT_AUTOMATION, schedule: new Date().toISOString() });
        }
        else {
            setEditAutomation(DEFAULT_AUTOMATION);
        }
    }

    function onEditAutomationClick(event) {
        setEditAutomation(findRowFromActionButton(event, automations));
    }

    function onLogsAutomationClick(event) {
        setEditAutomation(findRowFromActionButton(event, automations));
    }

    function onStartAutomationClick(event) {
        startAutomation(getIdFromActionButton(event))
            .then(automation => history.go(0))
            .catch(err => setNotification(getErrorMessage(processError(err))))
    }

    function onStopAutomationClick(event) {
        stopAutomation(getIdFromActionButton(event))
            .then(automation => history.go(0))
            .catch(err => setNotification(getErrorMessage(processError(err))))
    }

    function onDeleteAutomationClick(event) {
        deleteAutomation(getIdFromActionButton(event))
            .then(automation => history.go(0))
            .catch(err => setNotification(getErrorMessage(processError(err))))
    }

    async function onPromoteAutomationClick(event) {
        const id = getIdFromActionButton(event);
        const ok = await confirmDialog({
            title: 'Promover para Produção',
            message: 'Cria (ou atualiza) a versão de Produção desta automação e desativa a versão de Teste.\n\nA versão de Produção fica desativada — você ativa quando estiver pronto.\n\nConfirma?',
            confirmLabel: 'Promover',
            confirmVariant: 'success'
        });
        if (!ok) return;
        promoteAutomation(id)
            .then(() => history.go(0))
            .catch(err => setNotification(getErrorMessage(processError(err))));
    }

    async function onCopyAllToTestClick() {
        const ok = await confirmDialog({
            title: 'Copiar todas para Teste',
            message: `Vai criar (ou atualizar) versões de Teste de todas as ${prodCounts && prodCounts.total} automações de Produção.\n\nTodas ficam desativadas — você ativa quando quiser.\n\nConfirma?`,
            confirmLabel: 'Copiar todas',
            confirmVariant: 'warning'
        });
        if (!ok) return;
        setCopyingAll(true);
        copyAllAutomationsToTest()
            .then(result => {
                const msg = `${result.copied} copiada(s), ${result.updated} atualizada(s) de ${result.total} automações.`;
                setNotification({ type: 'success', text: msg });
                history.go(0);
            })
            .catch(err => setNotification(getErrorMessage(processError(err))))
            .finally(() => setCopyingAll(false));
    }

    // Provisiona IA pra um símbolo sem cobertura (short por IA aparece 🟡). Avisa do custo
    // de tokens antes — a análise roda a cada 20 min e consome a cota do provedor de IA.
    async function onProvisionAiClick(event) {
        const symbol = event.currentTarget.dataset.symbol;
        if (!symbol) return;
        const ok = await confirmDialog({
            title: `Provisionar IA para ${symbol}`,
            message: `Cria uma automação de Análise IA para ${symbol} (roda a cada 20 min) para gerar o sinal que o short por IA consome.\n\n⚠️ Isto CONSOME TOKENS de IA por ciclo — usa a cota/custo do seu provedor de IA enquanto estiver ativa.\n\nSem isto, a automação "Abrir Short (Regra)" já cobre o símbolo por regime (sem IA). Confirma?`,
            confirmLabel: 'Provisionar (consome tokens)',
            confirmVariant: 'warning'
        });
        if (!ok) return;
        provisionAiSignal(symbol, environment)
            .then(r => {
                setNotification({ type: 'success', text: r && r.alreadyCovered
                    ? `${symbol} já tem cobertura de IA ativa.`
                    : `Análise IA provisionada para ${symbol}. O sinal aparece no próximo ciclo (até 20 min).` });
                history.go(0);
            })
            .catch(err => setNotification(getErrorMessage(processError(err))));
    }

    async function onRunClick(event) {
        const id = getIdFromActionButton(event);
        runAutomation(id)
            .then(() => setNotification({ type: 'success', text: 'Automação disparada manualmente.' }))
            .catch(err => setNotification(getErrorMessage(processError(err))));
    }

    async function onCopyToTestClick(event) {
        const id = getIdFromActionButton(event);
        const ok = await confirmDialog({
            title: 'Copiar para Teste',
            message: 'Cria (ou atualiza) a versão de Teste desta automação para operar no testnet.\n\nA versão de Teste fica desativada — você ativa quando quiser.\n\nConfirma?',
            confirmLabel: 'Copiar para Teste',
            confirmVariant: 'warning'
        });
        if (!ok) return;
        copyAutomationToTest(id)
            .then(() => history.go(0))
            .catch(err => setNotification(getErrorMessage(processError(err))));
    }

    function onAutomationSubmit(event) {
        history.go(0);
    }

    return (
        <React.Fragment>
            <Menu />
            <main className='content'>
                <div className='d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4'>
                    <div className='d-block mb-4 mb-md-0'>
                        <h2 className='h4'>Automações</h2>
                    </div>
                    <div className='btn-toolbar mb-2 mb-md-0 align-items-center'>
                        <ViewModeToggle mode={viewMode} onChange={onViewModeChange} storageKey='automationsViewMode' />
                        <OnlyActiveToggle id='automationsOnlyActive' checked={onlyActive} onChange={onToggleActive} />
                        <div className='btn-group me-2'>
                            <SelectStrategy strategyId={strategyId} allLabel='Todas as estratégias' onChange={onStrategyChange} />
                        </div>
                        <div className='d-inline-flex align-items-center'>
                            <CompletarAutomacoesButton onSuccess={() => history.go(0)} />
                            <BacktestButton />
                            <NewAutomationButton onClick={onNewAutomationClick} />
                        </div>

                    </div>
                </div>
                <HowItWorks>
                    <p className='mb-1'><strong>Automações</strong> avaliam condições com base nos dados dos monitores e executam ações automaticamente.</p>
                    <ul className='mb-0 ps-3'>
                        <li><strong>Regular</strong> — reage a eventos de mercado (ex: preço cruzou uma média, sinal da IA mudou).</li>
                        <li><strong>Agendada</strong> — executa em horário fixo via cron (ex: comprar todo dia às 9h).</li>
                        <li><strong>Grid</strong> — cria uma grade de ordens em faixas de preço para aproveitar oscilações.</li>
                    </ul>
                    <p className='mt-2 mb-0'>Dica: use os <strong>Templates de Estratégia</strong> para criar automações pré-configuradas em um clique.</p>
                </HowItWorks>
                <SectionHealthBanner filterIds={AUTOMATION_ISSUE_IDS} title='Automações com pendências' />
                {hiddenCount > 0 && (
                    <div className='card border-0 shadow mb-3'>
                        <div className='card-body py-3'>
                            <p className='mb-1'>
                                🧩 <strong>{hiddenCount}</strong> automação(ões) gerenciada(s) pelas suas estratégias não {hiddenCount === 1 ? 'aparece' : 'aparecem'} nesta visão — gerencie pela tela <Link to='/strategies'>Estratégias</Link>.
                            </p>
                            <small className='text-muted d-block mb-2'>Uma estratégia agrupa o monitor e as automações de compra/venda em uma peça só, pra você não precisar mexer nelas separadamente.</small>
                            {getStrategyChips().length > 0 && (
                                <div>
                                    {getStrategyChips().map(chip => (
                                        <span key={chip.id} className='badge bg-light text-dark border me-1 mb-1'>{chip.name} ({chip.count})</span>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}
                {automations && automations.length === 0 && count === 0 && (
                    hiddenCount > 0 ? (
                        <div className='card border-0 shadow mb-4'>
                            <div className='card-body text-center py-4'>
                                <p className='text-muted mb-0'>Você não tem automações avulsas — tudo que roda aqui é gerenciado pelas suas estratégias.</p>
                            </div>
                        </div>
                    ) : environment === 'test'
                        ? (
                            <div className='card border-0 shadow mb-4'>
                                <div className='card-body text-center py-4'>
                                    <p className='text-muted mb-1'>Nenhuma automação de <strong>Teste</strong> ainda.</p>
                                    {prodCounts && prodCounts.total > 0 && (
                                        <div className='alert alert-info py-2 mb-2 text-start' style={{ fontSize: 13 }}>
                                            <div className='d-flex justify-content-between align-items-center flex-wrap gap-2'>
                                                <span>
                                                    Você tem <strong>{prodCounts.active} automação(ões) ativa(s)</strong> e <strong>{prodCounts.total} no total</strong> em Produção.
                                                </span>
                                                <button
                                                    className='btn btn-sm btn-warning'
                                                    disabled={copyingAll}
                                                    onClick={onCopyAllToTestClick}>
                                                    {copyingAll ? 'Copiando...' : `🧪 Copiar todas (${prodCounts.total}) para Teste`}
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                    <small className='text-muted'>
                                        Automações de teste são criadas com o campo <strong>Ambiente = Teste</strong> na tela de edição.
                                        Para começar a operar em teste, ative uma <Link to='/strategies'>estratégia</Link> ou crie uma nova automação definindo o ambiente como Teste.
                                    </small>
                                </div>
                            </div>
                        )
                        : <QuickSetupBanner onSuccess={() => history.go(0)} />
                )}
                {!(automations && automations.length === 0 && hiddenCount > 0) && (
                    <div className='card card-body border-0 shadow table-wrapper table-responsive'>
                        <table className='table table-hover'>
                            <thead>
                                <tr>
                                    <th className='border-gray-200'>Símbolo</th>
                                    <th className='border-gray-200'>Nome</th>
                                    <th className='border-gray-200'>Status</th>
                                    <th className='border-gray-200'>Ação</th>
                                    <th className='border-gray-200'>Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {
                                    automations && automations.length
                                        ? automations.map(automation => (<AutomationRow key={automation.id} data={automation} usages={usageMap[automation.id]} health={healthMap[automation.id]} onEditClick={onEditAutomationClick} onStopClick={onStopAutomationClick} onStartClick={onStartAutomationClick} onDeleteClick={onDeleteAutomationClick} onPromoteClick={onPromoteAutomationClick} onCopyToTestClick={onCopyToTestClick} onLogsClick={onLogsAutomationClick} onRunClick={onRunClick} onProvisionAiClick={onProvisionAiClick} />))
                                        : <React.Fragment></React.Fragment>
                                }
                            </tbody>
                        </table>
                        <Pagination count={count} size={size} />
                    </div>
                )}
                <Footer />
            </main>
            <AutomationModal data={editAutomation} onSubmit={onAutomationSubmit} />
            <GridModal data={editAutomation} onSubmit={onAutomationSubmit} />
            <LogModal file={editAutomation.id > 0 ? 'A_' + editAutomation.id : ''} logName={editAutomation.name} modalId='modalAutomationLogs' />
            <BacktestModal showStrategies={false} />
            <Toast type={notification.type} text={notification.text} />
        </React.Fragment>
    )
}

export default Automations;